import { useRef, useEffect } from 'react';
import { observable } from 'mobx';
import { navList } from '@src/constants/const';

import { getHashParams, updateHashParams } from '@src/utils/query-string-util.js';

class MainStore {
  /** 滚动锁定 */
  @observable isScrollLocked = false;
  /** 公共页脚是否展开 */
  @observable isFooterVisible = false;
  /** 当前页面索引 */
  @observable activeIndex = Math.max(0, navList.findIndex(item => item.key === (getHashParams().anchor || 'home')));
  @observable currentPage = navList[this.activeIndex].key;
  /** 上次滚动时间 */
  lastScrollTime = 0;
  /** 防抖时间 ms */
  intervalTime = 350;
  /** 页面容器引用 */
  pageContainerRef = null;
  /** 向下滚动 */
  arrowDownFn = () => {
    if (this.isScrollLocked || this.toggleFooter(true)) return;

    if (this.activeIndex < navList.length - 1) {
      this.activeIndex++;
      this.currentPage = navList[this.activeIndex].key;
      updateHashParams({ anchor: this.currentPage });
    }
  };
  /** 向上滚动 */
  arrowUpFn = () => {
    if (this.isScrollLocked || this.toggleFooter(false)) return;

    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.currentPage = navList[this.activeIndex].key;
      updateHashParams({ anchor: this.currentPage });
    }
  };

  /** 页脚开关及拦截：返回 true 表示已处理页脚逻辑，应拦截翻页 */
  toggleFooter = isOpen => {
    const footer = document.getElementById('OMNI__COMMON__FOOTER');
    const isShowing = footer?.classList.contains('show');

    // 开启：仅在最后一页时执行
    if (isOpen) {
      if (this.activeIndex !== navList.length - 1) return false;
      footer?.classList.add('show');
      this.isFooterVisible = !!footer;
      return true;
    }

    this.isFooterVisible = false;
    // 关闭：仅在当前已显示时执行
    if (isShowing) {
      footer?.classList.remove('show');
      return true;
    }

    return false;
  };
  /** 处理鼠标滚轮事件 */
  handleWheel = event => {
    // 保留适龄提示内容的原生滚动，避免触发背景翻页。
    if (event.target.closest?.('.c-age-tips-content')) return;
    event.preventDefault();
    // 获取当前时间
    const currentTime = Date.now();
    // 检查是否在500ms内连续滚动
    if (currentTime - this.lastScrollTime < this.intervalTime) return;
    // 更新上次滚动时间
    this.lastScrollTime = currentTime;
    const deltaY = event.deltaY;
    if (deltaY > 0) this.arrowDownFn();
    else this.arrowUpFn();
  };
  /** 监听键盘上下箭头事件 */
  handleKeyDown = event => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
    }
    // 获取当前时间
    const currentTime = Date.now();
    // 检查是否在500ms内连续操作
    if (currentTime - this.lastScrollTime < this.intervalTime) return;
    // 更新上次操作时间
    this.lastScrollTime = currentTime;
    if (event.key === 'ArrowDown') this.arrowDownFn();
    else if (event.key === 'ArrowUp') this.arrowUpFn();
  };
  /** 处理hashchange事件，更新当前页面索引 */
  handleHashChange = () => {
    const newAnchor = getHashParams().anchor || 'home';
    const newIndex = Math.max(0, navList.findIndex(item => item.key === newAnchor));
    if (newIndex === this.activeIndex && navList[newIndex].key === this.currentPage) return;
    this.activeIndex = newIndex;
    this.currentPage = navList[newIndex].key;
    this.toggleFooter(false);
  };

  /** 直接选中 */
  setCurrentPage(val) {
    this.toggleFooter(false);
    const newIndex = Math.max(0, navList.findIndex(item => item.key === val));
    this.activeIndex = newIndex;
    this.currentPage = navList[newIndex].key;
    updateHashParams({ anchor: this.currentPage });
  }
  lockScroll = () => {
    this.isScrollLocked = true;
  };
  unLockScroll = () => {
    this.isScrollLocked = false;
  };

  use() {
    this.pageContainerRef = useRef(null);

    useEffect(() => {
      // console.log('useMount', this.activeIndex, this.currentPage);

      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('hashchange', this.handleHashChange);
      window.addEventListener('wheel', this.handleWheel, { passive: false });
      return () => {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('hashchange', this.handleHashChange);
        window.removeEventListener('wheel', this.handleWheel);
      };
    }, []);
  }
}

export default MainStore;
